import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import MovieIcon from '@mui/icons-material/Movie';
import ImageIcon from '@mui/icons-material/Image';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import { useAppContext } from '../context/AppContext';
import { singingPhotoZeroApi, singingPhotoApi } from '../services/api';
import VideoPlayer from '../components/VideoPlayer';

// Utility functions
const processImageDataUri = (data_uri: string): string => {
  if (data_uri.startsWith('http://') || data_uri.startsWith('https://')) {
    return data_uri;
  }
  const clean = data_uri.startsWith('data:') ? data_uri.split(',')[1] : data_uri;
  return `data:image/png;base64,${clean}`;
};

const processVideoDataUri = (data_uri: string): string => {
  if (data_uri.startsWith('http://') || data_uri.startsWith('https://')) {
    return data_uri;
  }
  const clean = data_uri.startsWith('data:') ? data_uri.split(',')[1] : data_uri;
  return `data:video/mp4;base64,${clean}`;
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const PromptTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: theme.palette.divider },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main },
  },
}));

type GenerationStep = 'idle' | 'generating-image' | 'image-ready' | 'generating-video' | 'completed';

interface HistoryItem {
  id: string;
  videoUrl: string;
  imageUrl: string;
  timestamp: number;
  prompt: string;
  duration: number;
}

const RandomSingingPhoto: React.FC = () => {
  const { state, actions } = useAppContext();
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartTimeRef = useRef<number | null>(null);
  
  const [step, setStep] = useState<GenerationStep>('idle');
  const [prompt, setPrompt] = useState('');
  const [resolution, setResolution] = useState<'854x480' | '480x854'>('480x854');
  const [duration, setDuration] = useState<number>(10);
  const [randomStyle, setRandomStyle] = useState<boolean>(true);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [generationProgress, setGenerationProgress] = useState(0);
  
  const [imageTaskId, setImageTaskId] = useState<string | null>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [generatedImageDataUri, setGeneratedImageDataUri] = useState<string | null>(null);
  
  const [videoTaskId, setVideoTaskId] = useState<string | null>(null);
  const [generatedVideo, setGeneratedVideo] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, []);

  const clearPolling = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  const handleReset = () => {
    clearPolling();
    setStep('idle');
    setStatusMessage('');
    setErrorMessage('');
    setGenerationProgress(0);
    setImageTaskId(null);
    setGeneratedImage(null);
    setGeneratedImageDataUri(null);
    setVideoTaskId(null);
    setGeneratedVideo(null);
  };

  const handleNewGeneration = () => {
    // Clear previous results but keep the prompt
    clearPolling();
    setStatusMessage('');
    setErrorMessage('');
    setGenerationProgress(0);
    setImageTaskId(null);
    setGeneratedImage(null);
    setGeneratedImageDataUri(null);
    setVideoTaskId(null);
    setGeneratedVideo(null);
    
    // Immediately start new generation
    handleGenerateImage();
  };

  // Step 1: Poll for image generation status
  const startImagePolling = (taskId: string) => {
    setStatusMessage('Generating image... This usually takes 1-2 minutes');
    const pollInterval = 10000;
    const maxPollTime = 300000; // 5 minutes
    setGenerationProgress(0);
    pollStartTimeRef.current = Date.now();
    clearPolling();

    let currentIntervalId: any = null;
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 5;

    const pollForStatus = async () => {
      const currentTime = Date.now();
      const startTime = pollStartTimeRef.current ?? currentTime;
      const elapsedTime = currentTime - startTime;
      
      if (elapsedTime > maxPollTime) {
        if (currentIntervalId) {
          clearInterval(currentIntervalId);
          progressIntervalRef.current = null;
        }
        setErrorMessage('Image generation timeout - took too long');
        setStep('idle');
        return;
      }

      try {
        const { status, progress: progressVal, statusMessages, queue_position } = (await singingPhotoZeroApi.checkStatus(taskId)) as any;
        const progress = typeof progressVal === 'number' ? progressVal : undefined;
        consecutiveErrors = 0;
        
        if (typeof progress === 'number') setGenerationProgress(progress * 100);
        if (statusMessages) {
          setStatusMessage(statusMessages.detail_message || statusMessages.message || '');
        } else if (typeof progress === 'number') {
          setStatusMessage(`Generating image: ${Math.round(progress * 100)}%`);
        }

        if (status && status.toLowerCase() === 'in_queue') {
          const tagline = queue_position ? `${queue_position} ahead of you.` : "You're next.";
          setStatusMessage(`Waiting in queue. ${tagline}`);
          return;
        }

        if (status && status.toLowerCase() === 'cancelled') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setStatusMessage('Image generation cancelled');
          setErrorMessage('Task was cancelled');
          setStep('idle');
          return;
        }

        if (status && status.toLowerCase() === 'failed') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          const failureReason = statusMessages?.message || statusMessages?.detail_message || 'Unknown error';
          setStatusMessage(`Image generation failed: ${failureReason}`);
          setErrorMessage(`Task failed: ${failureReason}`);
          setStep('idle');
          return;
        }

        if (status && status.toLowerCase() === 'completed') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setStatusMessage('Image generated! Fetching result...');

          const result = await singingPhotoZeroApi.getResult(taskId);
          
          // Handle batch results array (take first image)
          let imageDataUri: string | null = null;
          if ((result as any).results && Array.isArray((result as any).results) && (result as any).results.length > 0) {
            imageDataUri = (result as any).results[0];
          } else {
            imageDataUri = (result as any).image?.data_uri || (result as any).image?.url;
          }

          if (imageDataUri) {
            const processedUri = processImageDataUri(imageDataUri);
            setGeneratedImage(processedUri);
            setGeneratedImageDataUri(imageDataUri);
            setStatusMessage('Image ready! Starting video generation...');
            setStep('image-ready');
            
            // Automatically start video generation
            setTimeout(() => {
              startVideoGeneration(imageDataUri);
            }, 1000);
          } else {
            setErrorMessage('Image generated but no data was provided');
            setStep('idle');
          }
        }
      } catch (err: any) {
        if (err.response && (err.response.status === 404 || err.response.status === 422)) {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setErrorMessage(`Task error: ${err.response?.data?.detail || 'Unknown error'}`);
          setStep('idle');
        } else {
          consecutiveErrors++;
          if (consecutiveErrors >= maxConsecutiveErrors) {
            if (currentIntervalId) {
              clearInterval(currentIntervalId);
              progressIntervalRef.current = null;
            }
            setErrorMessage(`Too many polling errors: ${err.message || 'Unknown error'}`);
            setStep('idle');
          } else {
            setErrorMessage(`Polling error: ${err.message || 'Unknown error'} - Will retry... (${consecutiveErrors}/${maxConsecutiveErrors})`);
          }
        }
      }
    };

    pollForStatus();
    const interval = setInterval(pollForStatus, pollInterval);
    currentIntervalId = interval;
    progressIntervalRef.current = interval;
  };

  // Step 2: Poll for video generation status
  const startVideoPolling = (taskId: string) => {
    setStatusMessage('Generating video... This usually takes 2-3 minutes');
    const pollInterval = 10000;
    const maxPollTime = 600000; // 10 minutes
    setGenerationProgress(0);
    pollStartTimeRef.current = Date.now();
    clearPolling();

    let currentIntervalId: any = null;
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 5;

    const pollForStatus = async () => {
      const currentTime = Date.now();
      const startTime = pollStartTimeRef.current ?? currentTime;
      const elapsedTime = currentTime - startTime;
      
      if (elapsedTime > maxPollTime) {
        if (currentIntervalId) {
          clearInterval(currentIntervalId);
          progressIntervalRef.current = null;
        }
        setErrorMessage('Video generation timeout - took too long');
        setStep('image-ready');
        return;
      }

      try {
        const { status, progress: progressVal, statusMessages, queue_position } = (await singingPhotoApi.checkStatus(taskId, 'singing-photo')) as any;
        const progress = typeof progressVal === 'number' ? progressVal : undefined;
        consecutiveErrors = 0;
        
        if (typeof progress === 'number') setGenerationProgress(progress * 100);
        if (statusMessages) {
          setStatusMessage(statusMessages.detail_message || statusMessages.message || '');
        } else if (typeof progress === 'number') {
          setStatusMessage(`Generating video: ${Math.round(progress * 100)}%`);
        }

        if (status && status.toLowerCase() === 'in_queue') {
          const tagline = queue_position ? `${queue_position} ahead of you.` : "You're next.";
          setStatusMessage(`Waiting in queue. ${tagline}`);
          return;
        }

        if (status && status.toLowerCase() === 'cancelled') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setStatusMessage('Video generation cancelled');
          setErrorMessage('Task was cancelled');
          setStep('image-ready');
          return;
        }

        if (status && status.toLowerCase() === 'failed') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          const failureReason = statusMessages?.message || statusMessages?.detail_message || 'Unknown error';
          setStatusMessage(`Video generation failed: ${failureReason}`);
          setErrorMessage(`Task failed: ${failureReason}`);
          setStep('image-ready');
          return;
        }

        if (status && status.toLowerCase() === 'completed') {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setStatusMessage('Video generation completed! Fetching result...');

          const { video } = await singingPhotoApi.getResult(taskId, 'singing-photo');
          const data_uri = video?.data_uri;

          if (video && data_uri) {
            const dataUriStr = data_uri as string;
            if (typeof dataUriStr !== 'string' || dataUriStr.length === 0) {
              setErrorMessage('Invalid video data received');
              setStep('image-ready');
              return;
            }

            setStatusMessage('Video generation completed!');
            const videoUrl = processVideoDataUri(dataUriStr);
            setGeneratedVideo(videoUrl);
            setStep('completed');
            
            // Add to history
            if (generatedImage) {
              const historyItem: HistoryItem = {
                id: Date.now().toString(),
                videoUrl,
                imageUrl: generatedImage,
                timestamp: Date.now(),
                prompt,
                duration,
              };
              setHistory(prev => [historyItem, ...prev].slice(0, 10)); // Keep last 10
            }
          } else {
            setErrorMessage('Video generated but no data was provided');
            setStep('image-ready');
          }
        }
      } catch (err: any) {
        if (err.response && (err.response.status === 404 || err.response.status === 422)) {
          if (currentIntervalId) {
            clearInterval(currentIntervalId);
            progressIntervalRef.current = null;
          }
          setErrorMessage(`Task error: ${err.response?.data?.detail || 'Unknown error'}`);
          setStep('image-ready');
        } else {
          consecutiveErrors++;
          if (consecutiveErrors >= maxConsecutiveErrors) {
            if (currentIntervalId) {
              clearInterval(currentIntervalId);
              progressIntervalRef.current = null;
            }
            setErrorMessage(`Too many polling errors: ${err.message || 'Unknown error'}`);
            setStep('image-ready');
          } else {
            setErrorMessage(`Polling error: ${err.message || 'Unknown error'} - Will retry... (${consecutiveErrors}/${maxConsecutiveErrors})`);
          }
        }
      }
    };

    pollForStatus();
    const interval = setInterval(pollForStatus, pollInterval);
    currentIntervalId = interval;
    progressIntervalRef.current = interval;
  };

  const handleGenerateImage = async () => {
    if (!prompt.trim() && prompt !== '') {
      alert('Please enter a prompt or leave it empty for random generation');
      return;
    }
    
    setErrorMessage('');
    setStatusMessage('Submitting image generation request...');
    setStep('generating-image');
    
    try {
      const [width, height] = resolution.split('x').map(Number);
      const { request_id } = await singingPhotoZeroApi.generateImage(
        prompt || 'random',
        width,
        height,
        randomStyle,
      );
      setImageTaskId(request_id);
      startImagePolling(request_id);
    } catch (error: any) {
      let errorDetail = 'Unknown error';
      if (error.response?.data?.detail) errorDetail = error.response.data.detail;
      else if (error.message) errorDetail = error.message;
      setErrorMessage(`Failed to start image generation: ${errorDetail}`);
      alert(`Error: ${errorDetail}`);
      setStep('idle');
    }
  };

  const startVideoGeneration = async (imageDataUri: string) => {
    setErrorMessage('');
    setStatusMessage('Submitting video generation request...');
    setStep('generating-video');
    
    try {
      // Check if it's a URL (presigned URL from R2) - pass it directly
      if (imageDataUri.startsWith('http://') || imageDataUri.startsWith('https://')) {
        // Pass the URL directly to the API (it will handle fetching server-side)
        const { request_id } = await singingPhotoApi.generateVideo(imageDataUri, '', 'singing-photo', duration, undefined, undefined);
        setVideoTaskId(request_id);
        startVideoPolling(request_id);
      } else {
        // Handle base64 data URI - convert to File
        const base64Data = imageDataUri.startsWith('data:') ? imageDataUri.split(',')[1] : imageDataUri;
        
        // Validate base64 string
        if (!base64Data || base64Data.length === 0) {
          throw new Error('Invalid image data: empty base64 string');
        }
        
        const byteString = atob(base64Data);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: 'image/png' });
        const file = new File([blob], 'generated-image.png', { type: 'image/png' });
        
        const { request_id } = await singingPhotoApi.generateVideo(file, '', 'singing-photo', duration, undefined, undefined);
        setVideoTaskId(request_id);
        startVideoPolling(request_id);
      }
    } catch (error: any) {
      let errorDetail = 'Unknown error';
      if (error.response?.data?.detail) errorDetail = error.response.data.detail;
      else if (error.message) errorDetail = error.message;
      setErrorMessage(`Failed to start video generation: ${errorDetail}`);
      alert(`Error: ${errorDetail}`);
      setStep('image-ready');
    }
  };

  const handleCancelImage = async () => {
    if (imageTaskId) {
      clearPolling();
      try {
        await singingPhotoZeroApi.cancelTask(imageTaskId);
        setStatusMessage('Image generation cancelled');
      } catch (err: any) {
        setErrorMessage('Error cancelling task: ' + (err.message || 'Unknown error'));
      }
      setStep('idle');
    }
  };

  const handleCancelVideo = async () => {
    if (videoTaskId) {
      clearPolling();
      try {
        await singingPhotoApi.cancelTask(videoTaskId, 'singing-photo');
        setStatusMessage('Video generation cancelled');
      } catch (err: any) {
        setErrorMessage('Error cancelling task: ' + (err.message || 'Unknown error'));
      }
      setStep('image-ready');
    }
  };

  const handleDownloadVideo = (videoUrl: string, filename: string = 'singing-photo.mp4') => {
    const link = document.createElement('a');
    link.href = videoUrl;
    link.download = filename;
    link.click();
  };

  const isGenerating = step === 'generating-image' || step === 'generating-video';

  return (
    <Grid container spacing={3} sx={{ height: 'calc(100vh - 120px)' }}>
      <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 3 }}>
        <StyledPaper elevation={3} sx={{ flex: '0 0 auto' }}>
          <Typography variant="h5" gutterBottom>
            Random Singing Photo Generator
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Generate a random image and automatically create a singing photo video
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <FormControl sx={{ width: 180 }}>
              <InputLabel id="resolution-label">Resolution</InputLabel>
              <Select
                labelId="resolution-label"
                id="resolution"
                value={resolution || '480x854'}
                label="Resolution"
                disabled={isGenerating}
                onChange={(e) => setResolution(e.target.value as '854x480' | '480x854')}
              >
                <MenuItem value="480x854">480x854 (Portrait)</MenuItem>
                <MenuItem value="854x480">854x480 (Landscape)</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ width: 180 }}>
              <InputLabel id="duration-label">Duration (s)</InputLabel>
              <Select
                labelId="duration-label"
                id="duration"
                value={duration || 10}
                label="Duration (s)"
                disabled={isGenerating}
                onChange={(e) => setDuration(Number(e.target.value))}
              >
                <MenuItem value={10}>10 seconds</MenuItem>
                <MenuItem value={20}>20 seconds</MenuItem>
                <MenuItem value={30}>30 seconds</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={randomStyle}
                  onChange={(e) => setRandomStyle(e.target.checked)}
                />
              }
              label="Random Style"
            />
          </Box>

          <PromptTextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="Enter your prompt here (leave empty for random)..."
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={isGenerating}
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            {step === 'idle' && (
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleGenerateImage}
                startIcon={<ImageIcon />}
                fullWidth
              >
                Generate Random Singing Photo
              </Button>
            )}
            {step === 'generating-image' && (
              <Button
                variant="outlined"
                color="error"
                size="large"
                onClick={handleCancelImage}
                fullWidth
              >
                Cancel Image Generation
              </Button>
            )}
            {step === 'generating-video' && (
              <Button
                variant="outlined"
                color="error"
                size="large"
                onClick={handleCancelVideo}
                fullWidth
              >
                Cancel Video Generation
              </Button>
            )}
            {(step === 'image-ready' || step === 'completed') && (
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleNewGeneration}
                startIcon={<RefreshIcon />}
                fullWidth
              >
                New Generation
              </Button>
            )}
          </Box>
        </StyledPaper>

        {/* History Section */}
        {history.length > 0 && (
          <StyledPaper elevation={3} sx={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <HistoryIcon />
              <Typography variant="h6">
                History
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({history.length} {history.length === 1 ? 'item' : 'items'})
              </Typography>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              overflowX: 'auto',
              overflowY: 'hidden',
              pb: 1,
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '4px',
              },
            }}>
              {history.map((item) => (
                <Paper
                  key={item.id}
                  elevation={2}
                  sx={{
                    minWidth: 150,
                    maxWidth: 150,
                    p: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                  }}
                >
                  <Box
                    component="img"
                    src={item.imageUrl}
                    alt="Thumbnail"
                    sx={{
                      width: '100%',
                      height: 100,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {item.prompt || 'Random'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.duration}s • {new Date(item.timestamp).toLocaleTimeString()}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadVideo(item.videoUrl, `singing-photo-${item.id}.mp4`)}
                    fullWidth
                  >
                    Save
                  </Button>
                </Paper>
              ))}
            </Box>
          </StyledPaper>
        )}
      </Grid>

      <Grid item xs={12} md={6} sx={{ height: '100%' }}>
        <StyledPaper elevation={3} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h5" gutterBottom>
            Result
          </Typography>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {isGenerating && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                {statusMessage || 'Processing...'}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={generationProgress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  mb: 1,
                  '& .MuiLinearProgress-bar': { animation: 'pulse 1.5s infinite ease-in-out' },
                }}
              />
              <Typography variant="body2" color="text.secondary" align="right">
                {`${Math.round(generationProgress)}%`}
              </Typography>
            </Box>
          )}

          {generatedImage && step !== 'completed' && (
            <Box sx={{ mt: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom>
                Generated Image
              </Typography>
              <Box
                component="img"
                src={generatedImage}
                alt="Generated"
                sx={{
                  width: '100%',
                  maxHeight: '400px',
                  objectFit: 'contain',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
              {step === 'generating-video' && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {statusMessage}
                </Typography>
              )}
            </Box>
          )}

          {generatedVideo && step === 'completed' && (
            <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Generated Video
                </Typography>
                <VideoPlayer videoUrl={generatedVideo} />
                <Typography variant="body2" color="success.main" sx={{ mt: 2 }}>
                  {statusMessage}
                </Typography>
              </Box>
              
              {generatedImage && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Source Image
                  </Typography>
                  <Box
                    component="img"
                    src={generatedImage}
                    alt="Source"
                    sx={{
                      width: '100%',
                      maxHeight: '300px',
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.9,
                      },
                    }}
                    onClick={() => {
                      // Open image in new tab for download/processing
                      const link = document.createElement('a');
                      link.href = generatedImage;
                      link.download = 'generated-image.png';
                      link.click();
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Click image to download
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {!isGenerating && !generatedImage && !generatedVideo && (
            <Box
              sx={{
                width: '100%',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
              }}
            >
              <Typography variant="body1">
                No content generated yet. Click "Generate Random Singing Photo" to start.
              </Typography>
            </Box>
          )}
        </StyledPaper>
      </Grid>
    </Grid>
  );
};

export default RandomSingingPhoto;
